import React, { useCallback, useEffect, useMemo, useState } from 'react';
import theme from './OptionGroupWizard.module.scss';
import {
    LoadingIndicator,
    SimpleWizard,
    SimpleWizardStep,
    Toast,
    ValidationScope,
} from '@ygtechnologies/bygr-frontend-component-library';
import {
    getOptionGroup,
    getOptionGroupOrderLocks,
    OptionGroupOrderLocks,
    OptionGroupUpdateParams,
} from '@/services/options/option-groups';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { getResponseErrorMessage } from '@/utils/apiError';
import { useAsyncStateWithIsLoading, useMergeState, useProjectRoomsEnabled } from '@/utils/hooks';
import { LargeRadio } from '@/components/forms/Radio';
import { OptionWizardState } from './OptionGroupWizard.types';
import { OptionGroupWizardProductsStep } from './OptionGroupWizardProductsStep';
import { OptionGroupWizardLinkRoomsStep } from './OptionGroupWizardLinkRoomsStep';
import { useSubmitDirectWizard, useSubmitRoomWizard } from '@/services/options/option-wizard.hooks';
import { OptionGroupWizardLinkUnitsStep } from './OptionGroupWizardLinkUnitsStep';
import { LinkingType, OptionGroupDto, ProjectTagType } from '@/types';
import { getOptionItemsByGroup, OptionItemCreateParams } from '@/services/options/option-items';
import { OptionGroupInfoEdit } from '../OptionGroupInfoEdit';
import { PricingFieldSelect } from '@/seller/components/project/ProjectSettings/PricingFieldSelect';
import { getProjectFeeSets } from '@/services/fees';
import { notEmptyPredicate } from '@ygtechnologies/rubus-frontend-utilities';
import { AlertBanner } from '@ygtechnologies/rubus-ui';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
enum Step {
    Products,
    GeneralInfo,
    LinkingType,
    LinkDirect,
    LinkRoom,
}

function getInitialData(roomsEnabled: boolean): OptionWizardState {
    return {
        // Projects without rooms enabled cannot link to rooms, so a new group defaults to (and is locked to)
        // direct linking.
        linkingType: roomsEnabled ? LinkingType.Room : LinkingType.Direct,
        details: {},
        products: [],
        directLinks: [],
        roomsLinks: [],
    };
}

const EMPTY_ORDER_LOCKS: OptionGroupOrderLocks = { unitIds: [], roomIds: [], optionItemIds: [] };

async function getSourceData(
    sourceOptionGroupId: number,
    isEditing: boolean | undefined,
    orderLocks: OptionGroupOrderLocks
): Promise<OptionWizardState> {
    const group = await getOptionGroup(sourceOptionGroupId);
    const options = await getOptionItemsByGroup(sourceOptionGroupId);
    const lockedOptionItemIds = new Set(orderLocks.optionItemIds);

    return {
        id: isEditing ? group.id : undefined,
        linkingType: group.linkType,
        details: {
            name: isEditing ? group.name : `Copy of ${group.name}`,
            internalNote: group.internalNote,
            description: group.description,
            projectFeeSetId: group.projectFeeSetId,
            hidePrice: group.hidePrices,
            makeFree: group.makeFree,
            allowManualQuantity: group.allowManualQuantity,
            disableManualSelection: group.disableManualSelection,
            providedByProfileId: group.providedByProfile?.id,
            roomId: undefined,

            deadlineId: group.deadline?.id,
            pricingVariableId: group.linkType === LinkingType.Direct ? group.pricingField?.id : null,
            showQuantityToBuyers: group.showQuantityToBuyers,
            projectTagId: group.tags?.find((t) => t.type === ProjectTagType.Room)?.id ?? null,
        },
        products: options.map((x) => ({
            isDefault: x.isDefault,
            price: x.basePrice || 0,
            product: x.product,
            isArchived: x.isArchived,
            // A product a unit has already ordered cannot be removed (it would orphan the signed order);
            // the seller can still archive it. Duplicate/create have no orders, so nothing is locked.
            isDeletable: !lockedOptionItemIds.has(x.id),
        })),
        directLinks: group.linkType === LinkingType.Direct ? group.linkedUnits.map((x) => x.residentialUnitId) : [],
        roomsLinks:
            group.linkType === LinkingType.Room
                ? group.linkedRooms.map((x) => ({ roomId: x.roomId, pricingFieldId: x.pricingFieldId }))
                : [],
    };
}

export interface OptionGroupWizardProps {
    projectId: number;
    sourceOptionGroupId?: number | null;
    onSubmit(result: OptionGroupDto): void;
    onCancel?(): void;
    isEditing?: boolean;
    nextStep?(): void;
}

export const OptionGroupWizard: React.FC<OptionGroupWizardProps> = ({
    projectId,
    sourceOptionGroupId,
    onSubmit,
    onCancel,
    isEditing,
}) => {
    const roomsEnabled = useProjectRoomsEnabled();
    const projectAbsolutePricing = useSelector((state: IState) => state.global.activeProject?.absolutePricing ?? true);
    const [data, mergeData, setData] = useMergeState<OptionWizardState>(getInitialData(roomsEnabled));
    const [orderLocks, setOrderLocks] = useState<OptionGroupOrderLocks>(EMPTY_ORDER_LOCKS);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState(Step.GeneralInfo);
    const [feeSets] = useAsyncStateWithIsLoading(() => getProjectFeeSets(projectId), [projectId], null);
    // Mode-aware price seeding applies only to a brand-new group (not duplicate/edit),
    // where every product is freshly added. The page's project is already in redux, so the
    // mode comes from activeProject (projectAbsolutePricing) rather than a second fetch.
    const isNewGroup = !sourceOptionGroupId && !isEditing;
    const seedAbsolutePricing = isNewGroup ? projectAbsolutePricing : undefined;
    const roomWizardMutation = useSubmitRoomWizard();
    const directWizardMutation = useSubmitDirectWizard();
    const selectedFeeSet = useMemo(() => {
        return feeSets?.find((x) => x.id === data.details.projectFeeSetId);
    }, [feeSets, data.details.projectFeeSetId]);

    const steps = [
        Step.GeneralInfo,
        Step.Products,
        // Kept in the edit flow too (with the type locked) so the linking config - and, for direct
        // groups, its whole-group pricing variable - stays reachable when editing an existing group.
        Step.LinkingType,
        data.linkingType === LinkingType.Direct ? Step.LinkDirect : Step.LinkRoom,
    ].filter(notEmptyPredicate);

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const group: OptionGroupUpdateParams = {
                id: data.id,
                projectId: projectId,
                deadlineId: data.details.deadlineId!,
                name: data.details.name ?? '',
                internalNote: data.details.internalNote ?? null,
                description: data.details.description ?? '',
                projectFeeSetId: data.details.projectFeeSetId ?? 0,
                providedByProfileId: data.details.providedByProfileId ?? null,
                disableManualSelection: data.details.disableManualSelection ?? false,
                hidePrice: data.details.hidePrice ?? false,
                makeFree: data.details.makeFree ?? false,
                allowManualQuantity: data.details.allowManualQuantity ?? false,
                showQuantityToBuyers: data.details.showQuantityToBuyers ?? false,
                projectTagId: data.details.projectTagId ?? null,
            };

            const options = data.products.map<OptionItemCreateParams>((x, index) => ({
                isDefault: x.isDefault,
                price: x.price,
                projectProductId: x.product.id,
                sortOrder: index,
                isArchived: x.isArchived,
            }));

            let result: OptionGroupDto;

            if (data.linkingType === LinkingType.Room) {
                result = await roomWizardMutation.mutateAsync({
                    optionGroup: group,
                    optionItems: options,
                    roomLinks: data.roomsLinks.map((x) => ({
                        pricingFieldId: x.pricingFieldId,
                        roomId: x.roomId,
                    })),
                });
            } else if (data.linkingType === LinkingType.Direct) {
                result = await directWizardMutation.mutateAsync({
                    optionGroup: {
                        ...group,
                        pricingVariableId: data.details.pricingVariableId ?? null,
                        roomId: 0,
                    },
                    optionItems: options,
                    linkedResidentialUnitIds: data.directLinks,
                });
            } else {
                throw Error('Invalid LinkingType');
            }

            onSubmit(result);
        } catch (err) {
            // Surface the backend's message (e.g. "Cannot unlink a unit that already has an order.") - the
            // wizard has no other error path, so without this the save fails silently.
            Toast.error(await getResponseErrorMessage(err, t`Could not save the option group.`));
        } finally {
            setSaving(false);
        }
    };

    const reset = useCallback(async () => {
        if (sourceOptionGroupId) {
            // Order locks only apply when editing the live group; a duplicate creates a fresh group with no orders.
            const locks = isEditing ? await getOptionGroupOrderLocks(sourceOptionGroupId) : EMPTY_ORDER_LOCKS;
            setOrderLocks(locks);
            setData(await getSourceData(sourceOptionGroupId, isEditing, locks));
        } else {
            setOrderLocks(EMPTY_ORDER_LOCKS);
            setData(getInitialData(roomsEnabled));
        }
        setLoaded(true);
        setStep(Step.GeneralInfo);
    }, [sourceOptionGroupId, isEditing, roomsEnabled]);

    useEffect(() => {
        reset();
    }, [reset]);

    const handleCancelClick = () => {
        onCancel?.();
    };

    if (!loaded) {
        return <LoadingIndicator />;
    }

    return (
        <ValidationScope>
            <SimpleWizard
                showProgressBar
                steps={steps}
                currentStep={step}
                onStepChange={(step) => setStep(step)}
                onSubmit={handleSubmit}
                onCancel={onCancel ? handleCancelClick : undefined}
                saveButtonProps={{
                    loading: saving,
                }}>
                {/* Step 1: General Info */}
                <SimpleWizardStep allowNavigationClick stepNumber={Step.GeneralInfo} header={<Trans>General info</Trans>}>
                    <OptionGroupInfoEdit
                        groupId={data.id}
                        linkingType={data.linkingType}
                        projectId={projectId!}
                        value={data.details}
                        onChange={(newValue) => mergeData({ details: newValue })}
                    />
                    <div className={theme.push} />
                </SimpleWizardStep>

                {/* Step 2: Products */}
                <SimpleWizardStep
                    allowNavigationClick={!!data.details.deadlineId && !!data.details.name}
                    stepNumber={Step.Products}
                    navigationHeader={<Trans>Products</Trans>}
                    header={<Trans>Choose products</Trans>}>
                    <OptionGroupWizardProductsStep
                        projectId={projectId!}
                        value={data.products}
                        selectedFeeSet={selectedFeeSet ?? null}
                        onChange={(newValue) => mergeData({ products: newValue })}
                        absolutePricing={seedAbsolutePricing}
                    />
                    <div className={theme.push} />
                </SimpleWizardStep>

                {/* Step 3: Linking Type */}
                <SimpleWizardStep
                    allowNavigationClick={!!data.products.length}
                    stepNumber={Step.LinkingType}
                    header={isEditing ? <Trans>Linking type</Trans> : <Trans>Choose linking type</Trans>}>
                    <div className={theme.linkingTypeContainer}>
                        {isEditing && (
                            <AlertBanner type="info">
                                <Trans>The linking type can&apos;t be changed after the option group is created.</Trans>
                            </AlertBanner>
                        )}
                        <div className={theme.radioContainer}>
                            <LargeRadio
                                disabled={!roomsEnabled || isEditing}
                                onChange={() => mergeData({ linkingType: LinkingType.Room })}
                                checked={data.linkingType === LinkingType.Room}>
                                {roomsEnabled ? (
                                    <Trans>
                                        <strong>Room linking</strong>
                                        The option group will be linked to rooms (recommended).
                                    </Trans>
                                ) : (
                                    <Trans>
                                        <strong>Room linking</strong>
                                        The option group will be linked to rooms.
                                    </Trans>
                                )}
                            </LargeRadio>

                            <LargeRadio
                                disabled={isEditing}
                                onChange={() => mergeData({ linkingType: LinkingType.Direct })}
                                checked={data.linkingType === LinkingType.Direct}>
                                <Trans>
                                    <strong>Direct linking</strong>
                                    The option group will be linked to units directly.
                                </Trans>
                            </LargeRadio>
                        </div>
                        {data.linkingType === LinkingType.Direct && (
                            <label>
                                <Trans>Pricing variable</Trans>
                                <PricingFieldSelect
                                    value={data.details.pricingVariableId || null}
                                    onChange={(pricingVariableId) =>
                                        mergeData({ details: { ...data.details, pricingVariableId } })
                                    }
                                    projectId={projectId}
                                />
                            </label>
                        )}
                        {roomsEnabled && !isEditing && (
                            <AlertBanner type="info">
                                <Trans>
                                    Room linking is recommended to make the option process more intuitive for your customers,
                                    as they will be able to make selections room by room.
                                </Trans>
                            </AlertBanner>
                        )}
                    </div>
                    <div className={theme.push} />
                </SimpleWizardStep>

                {/* Step 4: Link Units */}
                <SimpleWizardStep
                    allowNavigationClick={!!data.linkingType}
                    stepNumber={Step.LinkDirect}
                    navigationHeader={<Trans>Units</Trans>}
                    header={<Trans>Link to units</Trans>}>
                    <OptionGroupWizardLinkUnitsStep
                        projectId={projectId}
                        value={data.directLinks}
                        lockedUnitIds={orderLocks.unitIds}
                        onChange={(newValue) => mergeData({ directLinks: newValue })}
                    />
                    <div className={theme.push} />
                </SimpleWizardStep>

                {/* Step 4: Link Rooms */}
                <SimpleWizardStep
                    allowNavigationClick={!!data.linkingType}
                    stepNumber={Step.LinkRoom}
                    navigationHeader={<Trans>Rooms & pricing</Trans>}
                    header={<Trans>Link rooms & set pricing</Trans>}>
                    <OptionGroupWizardLinkRoomsStep
                        projectId={projectId!}
                        value={data.roomsLinks}
                        lockedRoomIds={orderLocks.roomIds}
                        onChange={(newValue) => mergeData({ roomsLinks: newValue })}
                    />
                </SimpleWizardStep>
            </SimpleWizard>
        </ValidationScope>
    );
};
